import React, { FC, MouseEvent } from 'react';
import { Tag as ChakraUITag } from '@chakra-ui/react';
import { TagVariant, TagSize } from './types';
import { Icon, IconProps } from '../Icon';

export interface TagProps {
  closeClick?: (event: MouseEvent) => void;
  variant?: TagVariant;
  colorScheme?: string;
  tagLabel?: string | React.ReactNode;
  isClosable?: boolean;
  size?: TagSize;
  children?: React.ReactNode;
  onClick?: (event: MouseEvent) => void;
  onMouseDown?: (event: MouseEvent) => void;
  textStyle?: string;
  isRound?: boolean;
  leftIcon?: IconProps;
  rightIcon?: IconProps;
  leftImage?: React.ReactNode;
  rightImage?: React.ReactNode;
  as?: React.ElementType;
  className?: string;
  ariaLabel?: string;
  'data-testid'?: string;
}

export const Tag: FC<TagProps> = ({
  closeClick,
  onClick,
  variant,
  colorScheme,
  tagLabel,
  isClosable,
  isRound,
  size,
  children,
  leftIcon,
  rightIcon,
  leftImage,
  rightImage,
  onMouseDown,
  className,
  ariaLabel,
  'data-testid': dataTestId,
}: TagProps) => {
  /**
   * DOM
   */
  const isInteractive = !!onClick || !!onMouseDown;
  return (
    <ChakraUITag.Root
      variant={variant as never}
      size={size}
      colorPalette={colorScheme}
      data-object-type="tag"
      {...(isRound && { rounded: 'full' })}
      {...(isInteractive && { as: 'button', cursor: 'pointer' })}
      {...(onClick && { onClick })}
      {...(onMouseDown && { onMouseDown })}
      {...(ariaLabel && { 'aria-label': ariaLabel })}
      {...(dataTestId && { 'data-testid': dataTestId })}
      className={className}
    >
      {leftImage || (leftIcon && <Icon {...leftIcon} />)}
      {!!tagLabel && <ChakraUITag.Label>{tagLabel}</ChakraUITag.Label>}
      {children}
      {rightImage || (rightIcon && <Icon {...rightIcon} />)}
      {!!isClosable && <ChakraUITag.CloseTrigger onClick={closeClick} {...(isRound && { rounded: 'full' })} />}
    </ChakraUITag.Root>
  );
};
